/**
 * Kanallar (28 Oct 2026): Etsy, the site, YouTube, Pinterest and CGTrader on one page (GET /channels). On top real
 * revenue month by month against the one year forecast (a bar per month, the expected case as a tick; the running
 * month is partial), then one card per channel: its state and last read, the headline numbers with their 30 day
 * change, a 30 day sparkline of its main number and its best items. A channel without its key or login says so and
 * links to its Bağlantılar card. Builds on the Site page's classes (api-usage.css, site-stats.css).
 */

import type {
  ChannelState,
  ChannelSummary,
  ChannelsView,
  Contribution,
  PinPublishing,
  RevenueMonth,
} from '@twin/contracts';
import { useEffect } from 'react';
import type { ConnectionStatus } from '../api/settings.js';
import { useChannels } from '../app/channels.js';
import { useIsDesktop } from '../app/media.js';
import { Link } from '../app/router.js';
import { formatNumber, formatRelativeClock } from '../lib/format.js';
import { Icon } from './icons.js';
import { StatusGlyph } from './settings.js';

const STATE_SHAPE: Readonly<Record<ChannelState, ConnectionStatus>> = Object.freeze({
  ok: 'connected',
  waiting: 'applying',
  not_connected: 'not_connected',
  error: 'error',
});

const STATE_LABEL: Readonly<Record<ChannelState, string>> = Object.freeze({
  ok: 'okunuyor',
  waiting: 'ilk okuma bekleniyor',
  not_connected: 'bağlı değil',
  error: 'sorun var',
});

function usd(value: number): string {
  return `${formatNumber(Math.round(value))} $`;
}

function value(v: number | null, unit: 'count' | 'usd'): string {
  if (v === null) return '-';
  return unit === 'usd' ? usd(v) : formatNumber(v);
}

function change(v: number | null, unit: 'count' | 'usd'): string | null {
  if (v === null || v === 0) return v === 0 ? '30 günde değişmedi' : null;
  const sign = v > 0 ? '+' : '−';
  return `30 günde ${sign}${unit === 'usd' ? usd(Math.abs(v)) : formatNumber(Math.abs(v))}`;
}

/** Revenue bars: actual per month, the expected case as a tick across the bar; months to come show the tick only. */
function RevenueChart({ months }: { months: readonly RevenueMonth[] }) {
  const max = Math.max(1, ...months.map((m) => Math.max(m.ordersCents ?? 0, m.expectedCents ?? 0)));
  const width = 320;
  const height = 96;
  const slot = width / Math.max(1, months.length);
  const bar = Math.max(4, slot * 0.6);
  const y = (cents: number) => height - (cents / max) * (height - 8);
  return (
    <figure className="ch-rev">
      <svg
        viewBox={`0 0 ${width} ${height + 14}`}
        className="chart-plot"
        role="img"
        aria-label="Aylık gelir ve beklenen"
      >
        {months.map((m, i) => {
          const x = i * slot + (slot - bar) / 2;
          return (
            <g key={m.month}>
              {m.ordersCents === null ? null : (
                <rect
                  x={x}
                  y={y(m.ordersCents)}
                  width={bar}
                  height={height - y(m.ordersCents)}
                  className={m.running ? 'ch-rev__bar ch-rev__bar--running' : 'ch-rev__bar'}
                />
              )}
              {m.expectedCents === null ? null : (
                <line
                  x1={x - 2}
                  x2={x + bar + 2}
                  y1={y(m.expectedCents)}
                  y2={y(m.expectedCents)}
                  className="ch-rev__expected"
                />
              )}
              <text x={x + bar / 2} y={height + 11} textAnchor="middle" className="ch-rev__label">
                {m.label.split(' ')[0]}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="sr-only">
        <table>
          <caption>Aylık gelir ve beklenen</caption>
          <thead>
            <tr>
              <th scope="col">Ay</th>
              <th scope="col">Gerçekleşen</th>
              <th scope="col">Beklenen</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month}>
                <th scope="row">{m.label}</th>
                <td>{m.ordersCents === null ? '-' : usd(m.ordersCents / 100)}</td>
                <td>{m.expectedCents === null ? '-' : usd(m.expectedCents / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function RevenueTable({ months }: { months: readonly RevenueMonth[] }) {
  return (
    <div className="sx-scroll">
      <table className="sx-table ch-rev-table">
        <thead>
          <tr>
            <th scope="col">Ay</th>
            <th scope="col">Gerçekleşen</th>
            <th scope="col">Sipariş</th>
            <th scope="col">Beklenen</th>
            <th scope="col">Oran</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => {
            const ratio =
              m.ordersCents !== null && m.expectedCents !== null && m.expectedCents > 0 && !m.running
                ? `%${Math.round((m.ordersCents / m.expectedCents) * 100)}`
                : m.running
                  ? 'sürüyor'
                  : '-';
            return (
              <tr key={m.month}>
                <th scope="row">{m.label}</th>
                <td className="mono">{m.ordersCents === null ? '-' : usd(m.ordersCents / 100)}</td>
                <td className="mono">{m.orders ?? '-'}</td>
                <td className="mono">{m.expectedCents === null ? '-' : usd(m.expectedCents / 100)}</td>
                <td className="mono">{ratio}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Sparkline({ label, points }: { label: string; points: ChannelSummary['series'] }) {
  if (points.length < 2) return <p className="sx-empty">{label}: grafik için en az iki gün gerekir.</p>;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const width = 300;
  const height = 40;
  const x = (i: number) => (i / (points.length - 1)) * width;
  const y = (v: number) => (max === min ? height / 2 : height - ((v - min) / (max - min)) * (height - 4) - 2);
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const last = points.at(-1);
  return (
    <figure className="ch-spark">
      <figcaption className="key ink-2 sx-sub">
        {label} · son {formatNumber(last?.value ?? 0)}
      </figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} className="chart-plot" role="img" aria-label={`${label}, 30 gün`}>
        <path d={d} className="ch-spark__line" />
      </svg>
    </figure>
  );
}

/** v0.1.265: the Pinterest pin queue under the Pinterest card. */
function PinQueue({ publishing, now }: { publishing: PinPublishing; now: number }) {
  return (
    <>
      <p className="sx-sublabel">Pin yayını</p>
      <p className="au-card__note">{publishing.environmentText}</p>
      {publishing.waitingText === null ? null : (
        <p className="au-card__note">Bekleme nedeni: {publishing.waitingText}</p>
      )}
      <ol className="sx-counts" aria-label="Pin yayını rakamları">
        <li className="sx-counts__row">
          <span className="sx-counts__key">Kuyrukta</span>
          <span className="sx-counts__value mono">{formatNumber(publishing.queued, 0)}</span>
        </li>
        <li className="sx-counts__row">
          <span className="sx-counts__key">Bugün yayınlanan</span>
          <span className="sx-counts__value mono">
            {formatNumber(publishing.publishedToday, 0)} / {formatNumber(publishing.dailyLimit, 0)}
          </span>
        </li>
        <li className="sx-counts__row">
          <span className="sx-counts__key">Son pin</span>
          <span className="sx-counts__value mono">
            {publishing.lastPublishedAt === null ? '-' : formatRelativeClock(publishing.lastPublishedAt, now)}
          </span>
        </li>
        {publishing.skipped7d + publishing.failed7d === 0 ? null : (
          <li className="sx-counts__row">
            <span className="sx-counts__key">Denetimde elenen / reddedilen (7 gün)</span>
            <span className="sx-counts__value mono">
              {formatNumber(publishing.skipped7d, 0)} / {formatNumber(publishing.failed7d, 0)}
            </span>
          </li>
        )}
      </ol>
    </>
  );
}

function ChannelCard({ channel, now }: { channel: ChannelSummary; now: number }) {
  const headingId = `ch-${channel.id}-h`;
  const shape = STATE_SHAPE[channel.state];
  return (
    <article className="au-card sx-card ch-card" aria-labelledby={headingId}>
      <header className="au-card__head">
        <div className="au-card__titles">
          <h3 id={headingId} className="au-card__name">
            {channel.url === null ? (
              channel.name
            ) : (
              <a href={channel.url} target="_blank" rel="noreferrer">
                {channel.name}
              </a>
            )}
          </h3>
          <p className="au-card__purpose">
            {channel.readAt === null ? 'Henüz okunmadı' : `Son okuma ${formatRelativeClock(channel.readAt, now)}`}
          </p>
        </div>
        <span className={`cpill cpill--${shape} au-chip`}>
          <StatusGlyph status={shape} inPill />
          {STATE_LABEL[channel.state]}
        </span>
      </header>
      {channel.stateText === '' ? null : <p className="au-card__note">{channel.stateText}</p>}
      {channel.fixPath === null ? null : (
        <Link to={channel.fixPath} className="sx-source__link">
          Ayarlar › Bağlantılar
          <Icon name="chevron-right" size={14} />
        </Link>
      )}
      <ol className="sx-counts" aria-label={`${channel.name} rakamları`}>
        {channel.metrics.map((m) => {
          const delta = change(m.change30, m.unit);
          return (
            <li key={m.label} className="sx-counts__row">
              <span className="sx-counts__key">
                {m.label}
                {delta === null ? null : <span className="ink-3"> · {delta}</span>}
              </span>
              <span className="sx-counts__value mono">{value(m.value, m.unit)}</span>
            </li>
          );
        })}
      </ol>
      {channel.publishing === undefined ? null : <PinQueue publishing={channel.publishing} now={now} />}
      {channel.series.length === 0 ? null : <Sparkline label={channel.seriesLabel} points={channel.series} />}
      {channel.items.length === 0 ? null : (
        <>
          <p className="sx-sublabel">{channel.itemsTitle}</p>
          <div className="sx-scroll">
            <table className="sx-table ch-items">
              <thead>
                <tr>
                  <th scope="col">Başlık</th>
                  {(channel.items[0]?.metrics ?? []).map((m) => (
                    <th key={m.label} scope="col">
                      {m.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {channel.items.map((item) => (
                  <tr key={`${item.title}-${item.url ?? ''}`}>
                    <th scope="row" className="sx-li--wide">
                      {item.url === null ? (
                        item.title
                      ) : (
                        <a href={item.url} target="_blank" rel="noreferrer">
                          {item.title}
                        </a>
                      )}
                    </th>
                    {item.metrics.map((m) => (
                      <td key={m.label} className="mono">
                        {m.value === null ? '-' : formatNumber(m.value, Number.isInteger(m.value) ? 0 : 2)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </article>
  );
}

function Revenue({ revenue }: { revenue: ChannelsView['revenue'] }) {
  return (
    <section aria-labelledby="ch-rev-h" className="sx-section">
      <h2 id="ch-rev-h" className="sx-section__title">
        Gelir ve 1 yıllık tahmin
      </h2>
      <p className="ch-rev__summary">{revenue.summary}</p>
      <RevenueChart months={revenue.months} />
      <p className="au-card__note">
        Çubuk: gerçekleşen (son ay sürüyor, açık renk). Çizgi: beklenen senaryo. {revenue.note}
      </p>
      <RevenueTable months={revenue.months} />
    </section>
  );
}

/**
 * v0.1.254: the contribution of the last 7 and 28 days (net sales less the estimated Etsy fees, refunds, ads and Twin's
 * own AI spend), computed by the database (buyer.contribution).
 */
/** Dollars with cents, so the small rows add up to Katkı (review of #141). */
function cents(value: number): string {
  return `${(value / 100).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

function ContributionCard({ contribution }: { contribution: NonNullable<ChannelsView['contribution']> }) {
  const rows: [string, (c: Contribution) => number, boolean][] = [
    ['Sipariş', (c) => c.orders, false],
    ['Net satış', (c) => c.netCents, true],
    ['Etsy kesintisi (tahmin)', (c) => -c.feesCents, true],
    ['İade', (c) => -c.refundCents, true],
    ['Reklam', (c) => -c.adCents, true],
    ['Yapay zekâ', (c) => -c.aiCents, true],
    ['Katkı', (c) => c.contributionCents, true],
  ];
  return (
    <section aria-labelledby="ch-contrib-h" className="sx-section">
      <h2 id="ch-contrib-h" className="sx-section__title">
        Katkı
      </h2>
      <div className="sx-scroll">
        <table className="sx-table ch-rev-table">
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col">Son 7 gün</th>
              <th scope="col">Son 28 gün</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, pick, money]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                {[contribution.week, contribution.month].map((c, index) => (
                  <td key={index === 0 ? 'week' : 'month'} className="mono">
                    {money ? cents(pick(c)) : formatNumber(pick(c))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="au-card__note">
        {contribution.feeNote} Kaynağı bilinmeyen sipariş (son 28 gün): {contribution.month.unknownChannel}.
      </p>
    </section>
  );
}

export function ChannelsPage() {
  const { store, state } = useChannels();
  const desktop = useIsDesktop();
  useEffect(() => {
    void store.reload();
  }, [store]);
  const view = state.view;
  if (view === null) {
    return state.error === null ? (
      <div className="loading" role="status">
        Yükleniyor…
      </div>
    ) : (
      <div className="shop-status" role="status">
        Kanallar yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={() => void store.reload()}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className={desktop ? 'au au--desk sx ch' : 'au sx ch'}>
      <Revenue revenue={view.revenue} />
      {view.contribution === undefined ? null : <ContributionCard contribution={view.contribution} />}
      <section aria-labelledby="ch-list-h" className="sx-section">
        <h2 id="ch-list-h" className="sx-section__title">
          Kanallar
        </h2>
        <ul className="au-list sx-list ch-list" aria-label="Kanallar">
          {view.channels.map((channel) => (
            <li key={channel.id}>
              <ChannelCard channel={channel} now={view.asOf} />
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => void store.reload()}
          disabled={state.loading}
        >
          {state.loading ? 'Yenileniyor…' : 'Yenile'}
        </button>
      </section>
    </div>
  );
}
